import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  output,
  OutputEmitterRef,
  ElementRef,
  viewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import '@mux/mux-player';

import { MuxPlayerEventDetail, MuxPlayerEvents } from './mux-player.models';
import { StreamType, PreloadType } from '../player/player.models';

@Component({
  selector: 'outlaw-mux-player',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './mux-player.component.html',
  styleUrl: './mux-player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MuxPlayerComponent implements AfterViewInit, OnDestroy {
  // Stream URL (full URL with token, alternative to playbackId + playbackToken)
  readonly src = input<string>();

  // Playback ID (used when src is not provided)
  readonly playbackId = input<string>();

  // Signed playback token (JWT, used with playbackId)
  readonly playbackToken = input<string>();

  // Optional inputs
  readonly streamType = input<StreamType>('on-demand');
  readonly autoplay = input<boolean>(false);
  readonly muted = input<boolean>(false);
  readonly loop = input<boolean>(false);
  readonly preload = input<PreloadType>('metadata');
  readonly poster = input<string>();
  readonly videoTitle = input<string>();
  readonly startTime = input<number>();

  // Controls visibility
  readonly controls = input<boolean>(true);

  // Styling inputs
  readonly accentColor = input<string>();
  readonly primaryColor = input<string>();
  readonly secondaryColor = input<string>();

  // Mux Data metadata
  readonly metadataVideoTitle = input<string>();
  readonly metadataViewerUserId = input<string>();

  // Outputs (events)
  readonly play = output<MuxPlayerEventDetail>();
  readonly pause = output<MuxPlayerEventDetail>();
  readonly ended = output<MuxPlayerEventDetail>();
  readonly timeUpdate = output<MuxPlayerEventDetail>();
  readonly error = output<MuxPlayerEventDetail>();
  readonly loadedMetadata = output<MuxPlayerEventDetail>();

  // Player element reference
  private readonly playerRef = viewChild<ElementRef<HTMLElement>>('player');
  private eventListeners: Array<{ event: string; handler: EventListener }> = [];

  ngAfterViewInit(): void {
    this.setupEventListeners();
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  private setupEventListeners(): void {
    const player = this.playerRef()?.nativeElement;
    if (!player) return;

    const standardEvents: Array<{
      name: string;
      output: OutputEmitterRef<MuxPlayerEventDetail>;
    }> = [
      { name: MuxPlayerEvents.Play, output: this.play },
      { name: MuxPlayerEvents.Pause, output: this.pause },
      { name: MuxPlayerEvents.Ended, output: this.ended },
      { name: MuxPlayerEvents.TimeUpdate, output: this.timeUpdate },
      { name: MuxPlayerEvents.LoadedMetadata, output: this.loadedMetadata },
    ];

    standardEvents.forEach(({ name, output }) => {
      const handler = (event: Event) => {
        const target = event.target as HTMLMediaElement;
        output.emit({
          currentTime: target?.currentTime,
          duration: target?.duration,
          ended: target?.ended,
        });
      };
      player.addEventListener(name, handler);
      this.eventListeners.push({ event: name, handler });
    });

    const errorHandler = () => {
      const playerEl = player as any;
      const mediaError = playerEl?.error;
      this.error.emit({
        currentTime: playerEl?.currentTime,
        duration: playerEl?.duration,
        ended: playerEl?.ended,
        error: mediaError ?? undefined,
        errorCode: mediaError?.code,
        muxErrorCode: mediaError?.muxCode,
      });
    };
    player.addEventListener(MuxPlayerEvents.Error, errorHandler);
    this.eventListeners.push({ event: MuxPlayerEvents.Error, handler: errorHandler });
  }

  private removeEventListeners(): void {
    const player = this.playerRef()?.nativeElement;
    if (!player) return;

    this.eventListeners.forEach(({ event, handler }) => {
      player.removeEventListener(event, handler);
    });
    this.eventListeners = [];
  }

  // Public API methods
  getPlayerElement(): HTMLElement | undefined {
    return this.playerRef()?.nativeElement;
  }
}
